import {MutableRefObject, useEffect, useRef} from 'react';

import throttle from 'lodash/throttle';
import {createGlobalStyle, GlobalStyleComponent} from 'styled-components';

interface Options {
  onChange?: (value: string) => unknown;
  stripBreakLines: boolean;
}

export function useAutoHeightTextarea({stripBreakLines, onChange}: Options): [
  {
    ref: MutableRefObject<HTMLTextAreaElement | null>;
    onChange: (ev: React.SyntheticEvent<HTMLTextAreaElement>) => void;
    className: string;
  },
  GlobalStyleComponent<Record<string, unknown>, any>
] {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (ref.current != null) {
      resize(ref.current);

      if (stripBreakLines) {
        // if break lines are removed the field will act as an input field
        // so it must submit the parent form on enter

        const handleKeyPress = (e: KeyboardEvent) => {
          if (e.key === 'Enter' && ref.current != null) {
            submitParentForm(ref.current);
          }
        };

        ref.current?.addEventListener('keyup', handleKeyPress);

        return () => ref.current?.removeEventListener('keyup', handleKeyPress);
      }
    }
  }, [ref.current]);

  function updatedOnChange(ev: React.SyntheticEvent<HTMLTextAreaElement>) {
    if (ev.target instanceof HTMLTextAreaElement) {
      let value = ev.target.value;

      if (stripBreakLines) {
        value = value.replace(/[\r\n]+/g, '');
      }

      if (onChange != null) {
        onChange(value);
      }

      // we cheat here - on normal circumstances we shouldn't update the
      // value directly and let that fot React, but here the value needs
      // be updated before calling the resize func.
      ev.target.value = value;
      throttledResize(ev.target);
    }
  }

  function getClassName(): string {
    // if the textarea is hidden it's not possible to detect its
    // height, so we show a normal scrollable textarea
    return ref.current != null && ref.current.offsetHeight > 0
      ? 'auto-height'
      : '';
  }

  return [
    {
      ref,
      onChange: updatedOnChange,
      className: getClassName(),
    },
    AutoHeightGlobalStyle,
  ];
}

function getElNumStyle(cs: CSSStyleDeclaration, styleName: string) {
  return parseInt(cs.getPropertyValue(styleName) || '0');
}

function resize(el: HTMLTextAreaElement) {
  if (el == null) {
    return;
  }

  el.rows = 1;
  el.style.height = 'auto';
  const contentHeight = Math.max(el.scrollHeight, el.offsetHeight);

  // if textarea is empty or it's hidden we'll stick with height:auto
  if (el.value.length === 0 || contentHeight === 0) {
    // if it's hidden let's show the first 3 lines
    if (contentHeight === 0) {
      el.rows = 3;
    }

    return;
  }

  const styles = window.getComputedStyle(el, null);
  const borderTop = getElNumStyle(styles, 'border-top-width');
  const borderBottom = getElNumStyle(styles, 'border-bottom-width');
  const verticalBorders = borderTop + borderBottom;
  el.style.height = `${contentHeight + verticalBorders}px`;
}

const throttledResize = throttle(resize, 100);

const AutoHeightGlobalStyle = createGlobalStyle`
    textarea.auto-height {
        overflow-x: hidden;
        overflow-y: auto;
        height: auto;
        resize: vertical;
        &::-webkit-scrollbar { width: 0 !important }
        overflow: -moz-scrollbars-none;
        -ms-overflow-style: none;
    }
`;

function submitParentForm(textarea: HTMLTextAreaElement) {
  // delay 10ms so React can finish its things first,
  // otherwise it will error out with a cryptic error
  setTimeout(
    // we first ensure 'closest' is present because IE doesn't support it
    () => textarea?.closest?.('form')?.requestSubmit(),
    10
  );
}
